import { Form, message, Button, Modal, Input, Card, Col, Row } from "antd";
import React, { useEffect, useState } from "react";
import SelectAjax from "../ui/SelectAjax";
import moment from "moment";
import certificationService from "../../services/certification.service";
import { useQuery } from "react-query";
import {
  useParams,
  useNavigate,
  Link,
  useSearchParams,
} from "react-router-dom";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import apiClient from "../../services/api";
import Search from "antd/lib/input/Search";
import CardInfo from "./CardInfo";
import CustomerForm from "../Admin/Customers/CustomerForm";
import { PageHeader } from "@ant-design/pro-components";

const GLPCreate = ({ mode = "create", back }) => {
  const [query] = useSearchParams();
  const [form] = Form.useForm();
  const [plateLoading, setPlateLoading] = useState(false);
  const [duaLoading, setDuaLoading] = useState(false);
  const [customerUrl, setCustomerUrl] = useState("customers-list");
  const [customerIsVisible, setCustomerIsVisible] = useState(false);
  const [sunarpLoading, setSunarpLoading] = useState(false);

  let { id } = useParams();

  const navigate = useNavigate();

  const { data, isFetching } = useQuery(
    ["certifications", id],
    () => certificationService.find(id),
    { keepPreviousData: false, enabled: id > 0, refetchOnWindowFocus: false }
  );

  useEffect(() => {
    if (data) {
      form.setFieldsValue(data);
      document.title = `#${data?.id} / ${data?.owner1} / Editar`;
    } else {
      document.title = `Crear GLP`;
    }

    if (data && query.get("id")) {
      form.setFieldsValue({ certification_type_id: query.get("id") });
    }
  }, [data, form, query]);

  const finish = async (e) => {
    try {
      let res;
      if (mode === "create") {
        res = await certificationService.create(e);
      } else {
        res = await certificationService.update(id, e);
      }

      if (res.success) {
        message.success(res.message);
        if (mode === "create") {
          navigate("/certifications/resolve/" + res.data.id);
          return;
        }

        form.setFieldsValue(res.data);

        return;
      }

      message.error(res.message);
    } catch (error) {
      message.error(error.message);
    }
  };

  const removeModel = async () => {
    Modal.confirm({
      title: "Confirmar borrado",
      icon: <ExclamationCircleOutlined />,
      content: "Se borrará este registro y los registros relacionados.",
      okText: "SI",
      cancelText: "NO",
      onOk: async () => {
        const res = await certificationService.remove(id);

        if (!res.success) {
          message.error(res.message ?? "Error al eliminar registro");
        }
        message.success(res.message ?? "Borrado exitoso");
        navigate("/certifications");
      },
    });
  };

  const searchDua = async (dua) => {
    setDuaLoading(true);

    try {
      const res = await apiClient.get("/dua-search", { params: { dua } });
      form.setFieldsValue(res.data);
    } catch (error) {
      message.warn("No se pudo obtener info de DUA, intenta mas tarde.");
    }

    setDuaLoading(false);
  };

  const searchDUA1 = async (dua) => {
    dua = (dua || "").trim();
    if (!dua) return message.warning("Ingresa un número de DUA");

    setDuaLoading(true);
    try {
      // Tu backend: GET /api/consultar-dua/{dua}  (o /api/dua/{dua} si hiciste alias)
      const { data: v } = await apiClient.get(`/consultar-dua/${dua}`);

      // Helpers
      const tonToKg = (x) =>
        x != null && x !== ""
          ? Math.round(parseFloat(String(x).replace(",", ".")) * 1000)
          : "";

      const norm = (x) => (x == null ? "" : x);

      const power = (p) => (p == null ? "" : String(p).replace(",", ".")); // "80,00@6000" -> "80.00@6000"

      form.setFieldsValue({
        // Identificación
        brand: norm(v.marca),
        model: norm(v.modelo),
        motor: norm(v.motor),
        chassis: norm(v.numero_serie) || norm(v.vin),

        // Especificaciones
        color: norm(v.color),
        bodywork: norm(v.carroceria),
        fuel: norm(v.combustible),
        version: norm(v.version),
        class: norm(v.categoria),

        // Años
        year_fabric: norm(v.anio_modelo), // si necesitas otro campo, duplica
        year_model: norm(v.anio_modelo),

        // Dimensiones (m)
        longitude: norm(v.longitud_m),
        height: norm(v.alto_m),
        width: norm(v.ancho_m),

        // Capacidades
        axes_number: norm(v.ejes),
        wheels_number: norm(v.ruedas),
        seats_number: norm(v.asientos),
        passenger_number: norm(v.pasajeros),
        cylinder_number: norm(v.cilindros),

        // Motor
        engine_size: norm(v.cilindrada_litros), // en litros; si quieres cc: Math.round(v*1000)
        power: power(v.potencia),

        // Pesos (t -> kg)
        gross_weight: tonToKg(v.peso_bruto_t),
        dry_weight: tonToKg(v.peso_neto_t),
        payload: tonToKg(v.carga_util_t),

        // Serie/VIN redundante si lo usas en otro campo
        serie: norm(v.numero_serie) || norm(v.vin),
      });

      message.success("Información del DUA cargada");
    } catch (e) {
      message.error(
        e?.response
          ? `Error: ${e.response.status}`
          : "No se pudo consultar el DUA"
      );
    } finally {
      setDuaLoading(false);
    }
  };

  const searchPlate = async (e) => {
    const plate = e;

    if (plate.length === 6) {
      setPlateLoading(true);

      const res = await apiClient.get("/mtc-search", {
        params: { plate: plate },
      });

      if (res.data.Marca?.length > 2) {
        form.setFieldsValue({
          // owner1: '',
          class: res.data.Categoria,
          model: res.data.Modelo,
          brand: res.data.Marca,
          // serie: '',
          chassis: res.data.VINSerCha,
          motor: res.data.NumeroMotor,
          color: res.data.Color,
          bodywork: res.data.Carroceria,
          fuel: res.data.Combustible,
          year_fabric: res.data.AñoFab,
          year_model: "",
          traction: "",
          axes_number: res.data.NumeroEjes,
          wheels_number: res.data.NumeroRuedas,
          seats_number: res.data.NumeroAsientos,
          engine_size: "", //cilindrada
          longitude: res.data.Largo,
          height: res.data.Alto,
          width: res.data.Ancho,
          passenger_number: res.data.NumeroPasajeros,
          cylinder_number: "",
          gross_weight: res.data.PesoBruto * 1000,
          dry_weight: res.data.PesoNeto * 1000,
          payload: res.data.PesoUtil * 1000,
        });
      }

      setPlateLoading(false);
    }
  };

  const searchPlateMtc = async (e) => {
    const plate = e;

    if (plate.length === 6) {
      setPlateLoading(true);

      try {
        const response = await apiClient.get("/mtc-search", {
          params: { plate },
        });

        console.log("Respuesta completa:", response.data);

        if (response.data.exito) {
          const data = response.data.datos;

          console.log("Datos del vehículo:", data);
          form.setFieldsValue({
            class: data.categoria || "",
            brand: data.marca || "",
            model: data.modelo || "",
            motor: data.numero_motor || "",
            chassis: data.vin_serie_chasis || "",
            serie: data.vin_serie_chasis || "",
            color: data.color || "",
            bodywork: data.carroceria || "",
            fuel: data.combustible || "",
            year_fabric: data.AñoFab || "",
            axes_number: data.numero_ejes || "",
            wheels_number: data.numero_ruedas || "",
            seats_number: data.numero_asientos || "",
            engine_size: "",
            longitude: data.largo || "",
            height: data.alto || "",
            width: data.ancho || "",
            passenger_number: data.numero_pasajeros || "",
            cylinder_number: "",

            gross_weight: data.peso_bruto ? Number(data.peso_bruto) * 1000 : "",
            dry_weight: data.peso_neto ? Number(data.peso_neto) * 1000 : "",
            payload: data.peso_util ? Number(data.peso_util) * 1000 : "",
          });

          message.success("Información de placa cargada correctamente");
        } else {
          message.warning(
            response.data.mensaje || "No se encontraron datos para esta placa"
          );
        }
      } catch (error) {
        console.error("Error al buscar la placa:", error);
        message.error("Error al obtener información de la placa");
      } finally {
        setPlateLoading(false);
      }
    } else {
      message.info("La placa debe tener 6 caracteres");
    }
  };

  const changeWeight = (e) => {
    const gross = Number(form.getFieldValue("gross_weight"));
    const dry = Number(form.getFieldValue("dry_weight"));
    form.setFieldsValue({ payload: gross - dry });
  };

  const searchSunarpPlate = async (plate) => {
    if (!plate || plate.length !== 6) {
      message.warning("La placa debe tener 6 caracteres");
      return;
    }

    setSunarpLoading(true);

    const toFloat = (v) => {
      if (v === null || v === undefined || v === "") return null;
      const n = parseFloat(String(v).replace(",", "."));
      return Number.isNaN(n) ? null : n;
    };

    try {
      const response = await apiClient.get("/sunarp/consulta", {
        params: { placa: plate },
      });

      // { message, data: <json del servicio>, fuente: 'url1' | 'url2' }
      const payload = response?.data;
      const fuente = payload?.fuente; // opcional, por si quieres usarlo
      const raw = payload?.data;

      let vehicleData = null;
      let ownerData = null;

      //  CASO 2: formato centralmontgall (listaAni[0])
      if (fuente === "url2" || raw?.listaAni) {
        vehicleData = raw?.listaAni?.[0] ?? null;
        ownerData =
          vehicleData?.listProp?.[0] ?? vehicleData?.LPropietario?.[0] ?? null;
      } else {
        // CASO 1: formato ngrok antiguo (data.data_placa.dataSunarp)
        const root = raw;
        const dataPlaca = root?.data?.data_placa ?? root?.data_placa ?? null;
        const dataSunarp = dataPlaca?.dataSunarp ?? dataPlaca ?? null;

        vehicleData = dataSunarp;
        ownerData =
          vehicleData?.listProp?.[0] ?? vehicleData?.LPropietario?.[0] ?? null;
      }

      console.log("Datos SUNARP recibidos:", payload);

      if (vehicleData) {
        // Pesos vienen en toneladas
        const pesoBrutoTn = toFloat(vehicleData.pesoBruto);
        const pesoSecoTn = toFloat(vehicleData.pesoSeco);

        const grossWeight =
          pesoBrutoTn !== null ? Math.round(pesoBrutoTn * 1000) : "";
        const dryWeight =
          pesoSecoTn !== null ? Math.round(pesoSecoTn * 1000) : "";
        const calculatedPayload =
          grossWeight !== "" && dryWeight !== "" ? grossWeight - dryWeight : "";

        form.setFieldsValue({
          owner1: ownerData?.propietario || ownerData?.NombrePropietario || "",

          brand: vehicleData.marca || "",
          model: vehicleData.modelo || "",
          motor: vehicleData.numMotor || "",
          chassis: vehicleData.numSerie || vehicleData.noVin || "",
          color: vehicleData.color || "",
          bodywork: vehicleData.descTipoCarr || "",
          fuel: vehicleData.descTipoComb || "",
          year_fabric: vehicleData.anoFab ?? vehicleData.anMode ?? "",
          year_model: vehicleData.anMode ?? "",
          axes_number: vehicleData.ejes ?? "",
          wheels_number: vehicleData.numRuedas ?? "",
          seats_number: vehicleData.numAsientos ?? "",
          passenger_number: vehicleData.numPasajeros ?? "",
          cylinder_number: vehicleData.numCilindros ?? "",
          power: vehicleData.poMotr || "",
          version: vehicleData.version || "",
          class: vehicleData.coCateg || "",
          serie: vehicleData.numSerie || vehicleData.noVin || "",

          longitude: vehicleData.longitud ?? "",
          height: vehicleData.altura ?? "",
          width: vehicleData.ancho ?? "",

          gross_weight: grossWeight,
          dry_weight: dryWeight,
          payload: calculatedPayload,
        });

        message.success(
          `Datos del vehículo cargados desde SUNARP (${fuente || "desconocida"})`
        );
      } else {
        message.warning("El servidor no encuentra datos del vehículo.");
      }
    } catch (err) {
      console.error("Error SUNARP:", err);
      message.error("Error al consultar SUNARP");
    } finally {
      setSunarpLoading(false);
    }
  };

  const onClickAddCustomer = () => {
    setCustomerIsVisible(true);
  };

  return (
    <div>
      <PageHeader
        ghost={false}
        onBack={() => navigate(back)}
        title={mode === "create" ? "Nuevo ingreso" : data?.certification?.name}
        subTitle={data?.date ? moment(data?.date).format("LL") : ""}
        extra={[
          mode === "edit" && (
            <Button
              disabled={isFetching}
              onClick={removeModel}
              danger
              key="cancel"
            >
              Anular
            </Button>
          ),

          mode === "edit" && (
            <Button disabled={isFetching} key="modifications">
              <Link to={"/certifications/resolve/" + id}>Modificaciones</Link>
            </Button>
          ),
          <Button
            disabled={isFetching}
            key="save"
            type="primary"
            onClick={() => form.submit()}
          >
            Guardar
          </Button>,
        ]}
      />

      <Form
        size="small"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 14 }}
        form={form}
        name="control-hooks"
        initialValues={{
          certification_type_id: 16,
          //place: "B3G019",
          transport_type_id: 1,
        }}
        onFinish={finish}
      >
        <Card
          loading={isFetching}
          className="cardex"
          title={
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div>Datos básicos</div>{" "}
              {mode === "edit" && (
                <div style={{ fontWeight: 300 }}>
                  Operación: {data?.operation_code}
                </div>
              )}
            </div>
          }
        >
          <Form.Item
            labelCol={{ span: 3 }}
            name="certification_type_id"
            label="Tipo de cert."
            rules={[{ required: true }]}
          >
            <SelectAjax
              size="middle"
              style={{ maxWidth: 400 }}
              url={"/certification-types?category_id=2"}
            />
          </Form.Item>

          <Form.Item
            labelCol={{ span: 2 }}
            name="company_id"
            label="Empresa"
            rules={[{ required: true }]}
          >
            <SelectAjax
              size="middle"
              showSearch
              optionFilterProp="children"
              url={"/companies-list"}
              style={{ maxWidth: 250 }}
            />
          </Form.Item>

          <Form.Item
            labelCol={{ span: 2 }}
            name="garage_id"
            label="Taller"
            rules={[{ required: true }]}
          >
            <SelectAjax
              size="middle"
              showSearch
              optionFilterProp="children"
              style={{ maxWidth: 400 }}
              url={"/garages-list"}
            />
          </Form.Item>

          <Form.Item
            labelCol={{ span: 2 }}
            name="customer_id"
            label="Cliente"
            rules={[{ required: true }]}
          >
            <SelectAjax
              onNew={onClickAddCustomer}
              size="middle"
              showSearch
              valueProp="id"
              optionFilterProp="children"
              style={{ marginRight: 5 }}
              url={customerUrl}
            />
          </Form.Item>
          <CustomerForm
            title={"Registrar nuevo cliente"}
            method={"POST"}
            onSuccess={(customer) => {
              setCustomerUrl((url) => url + "?nc=" + Math.random() * 100);
              form.setFieldsValue({ customer_id: customer.id });
              setCustomerIsVisible(false);
            }}
            onCancel={() => {
              setCustomerIsVisible(false);
            }}
            visible={customerIsVisible}
          />

          <Row gutter={4} align="bottom">
            <Col span={7}>
              <Form.Item
                labelCol={{ span: 7 }}
                name="plate"
                label="Placa"
                rules={[
                  {
                    message: "Ingresa placa válida",
                    required: true,
                    pattern: /^[0-9a-zA-Z\-]{6,50}$/,
                  },
                ]}
              >
                <Search
                  loading={plateLoading}
                  onSearch={searchPlateMtc}
                  placeholder="B3G019"
                  style={{ width: 200 }}
                />
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item>
                <Button
                  type="primary"
                  size="middle"
                  loading={sunarpLoading}
                  onClick={() => {
                    const plateValue = form.getFieldValue("plate");
                    if (plateValue && plateValue.length === 6) {
                      searchSunarpPlate(plateValue);
                    } else {
                      message.warning(
                        "Ingresa una placa válida de 6 caracteres"
                      );
                    }
                  }}
                >
                  SUNARP
                </Button>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            //initialValue={"118-2021-10-012468-1"}
            labelCol={{ span: 2 }}
            name="dua"
            label="DUA"
            rules={[{ message: "Ingresa dua válida", required: false }]}
          >
            <Search
              loading={duaLoading}
              onSearch={searchDUA1}
              placeholder="Ingresa DUA"
              style={{ width: 250 }}
            />
          </Form.Item>
        </Card>

        <Card
          loading={isFetching}
          className="cardex"
          title="Datos del vehículo"
          style={{ marginTop: 20 }}
        >
          <Form.Item
            name="owner1"
            labelCol={{ span: 2 }}
            label="Titular"
            rules={[{ required: true }]}
          >
            <Input size="middle" />
          </Form.Item>

          <Row>
            <Col span={16}>
              <Row>
                <Col span={10}>
                  <Form.Item
                    name="class"
                    label="Cat./Clase"
                    rules={[{ required: false }]}
                  >
                    <Input />
                  </Form.Item>

                  <Form.Item
                    name="model"
                    label="Modelo"
                    rules={[{ required: false }]}
                  >
                    <Input />
                  </Form.Item>

                  <Form.Item
                    name="brand"
                    label="Marca"
                    rules={[{ required: false }]}
                  >
                    <Input />
                  </Form.Item>

                  <Form.Item
                    name="serie"
                    label="Serie"
                    rules={[{ required: false }]}
                  >
                    <Input />
                  </Form.Item>

                  <Form.Item
                    name="chassis"
                    label="Chasis"
                    rules={[{ required: false }]}
                  >
                    <Input />
                  </Form.Item>

                  <Form.Item
                    name="motor"
                    label="Motor"
                    rules={[{ required: false }]}
                  >
                    <Input />
                  </Form.Item>

                  <Form.Item
                    name="color"
                    label="Color"
                    rules={[{ required: false }]}
                  >
                    <Input />
                  </Form.Item>

                  <Form.Item
                    name="bodywork"
                    label="Carrocería"
                    rules={[{ required: false }]}
                  >
                    <Input />
                  </Form.Item>

                  <Form.Item
                    name="fuel"
                    label="Combustible"
                    rules={[{ required: false }]}
                  >
                    <Input />
                  </Form.Item>

                  <Form.Item
                    name="power"
                    label="Potencia"
                    rules={[{ required: false }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>

                <Col span={14}>
                  <Row>
                    <Col span={12}>
                      <Form.Item
                        name="year_fabric"
                        label="Año Fab"
                        rules={[{ required: false }]}
                      >
                        <Input />
                      </Form.Item>

                      <Form.Item
                        name="year_model"
                        label="Año Mod"
                        rules={[{ required: false }]}
                      >
                        <Input />
                      </Form.Item>

                      <Form.Item
                        name="axes_number"
                        label="Ejes"
                        rules={[{ required: false }]}
                      >
                        <Input />
                      </Form.Item>

                      <Form.Item
                        name="wheels_number"
                        label="Ruedas"
                        rules={[{ required: false }]}
                      >
                        <Input />
                      </Form.Item>

                      <Form.Item
                        name="seats_number"
                        label="Asientos"
                        rules={[{ required: false }]}
                      >
                        <Input />
                      </Form.Item>

                      <Form.Item
                        name="engine_size"
                        label="Cilindrada"
                        rules={[{ required: false }]}
                      >
                        <Input />
                      </Form.Item>

                      <Form.Item
                        name="longitude"
                        label="Longitud"
                        rules={[{ required: false }]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>

                    <Col span={12}>
                      <Form.Item
                        name="height"
                        label="Alto"
                        rules={[{ required: false }]}
                      >
                        <Input />
                      </Form.Item>

                      <Form.Item
                        name="width"
                        label="Ancho"
                        rules={[{ required: false }]}
                      >
                        <Input />
                      </Form.Item>

                      <Form.Item
                        name="passenger_number"
                        label="Pasajeros"
                        rules={[{ required: false }]}
                      >
                        <Input />
                      </Form.Item>

                      <Form.Item
                        name="cylinder_number"
                        label="Cilindros"
                        rules={[{ required: false }]}
                      >
                        <Input />
                      </Form.Item>

                      <Form.Item
                        name="gross_weight"
                        label="P. Bruto"
                        rules={[{ required: false }]}
                      >
                        <Input onChange={changeWeight} />
                      </Form.Item>

                      <Form.Item
                        name="dry_weight"
                        label="P. Seco"
                        rules={[{ required: false }]}
                      >
                        <Input onChange={changeWeight} />
                      </Form.Item>

                      <Form.Item
                        name="payload"
                        label="Carga útil"
                        rules={[{ required: false }]}
                      >
                        <Input />
                      </Form.Item>

                      <Form.Item
                        name="version"
                        label="Version"
                        rules={[{ required: false }]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Col>
          </Row>
        </Card>
      </Form>

      <CardInfo certification={data} />
    </div>
  );
};

export default GLPCreate;
